<script setup lang="ts">
/**
 * Switch — boolean toggle. Used for feature flags, "is_active",
 * "is_test" and the dozens of tinyint(1) flags in the legacy schema.
 *
 * Reka's SwitchRoot/SwitchThumb give us the keyboard handling and
 * aria-checked plumbing; we style it to match the prototype's
 * crimson-on-dark palette.
 */
import { SwitchRoot, SwitchThumb } from 'reka-ui'

interface Props {
  modelValue: boolean
  disabled?: boolean
  /** ID for label association. Generated if omitted. */
  id?: string
}

const props = defineProps<Props>()

defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const fieldId =
  props.id ??
  `switch-${Math.random().toString(36).slice(2, 10)}`
</script>

<template>
  <SwitchRoot
    :id="fieldId"
    :checked="modelValue"
    :aria-checked="String(modelValue)"
    :disabled="disabled"
    class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center
           rounded-full border border-border transition-colors
           data-[state=checked]:bg-brand data-[state=checked]:border-brand-line
           data-[state=unchecked]:bg-surface-3
           focus-visible:outline-none focus-visible:shadow-focus
           disabled:cursor-not-allowed disabled:opacity-50"
    @update:checked="(v: boolean) => $emit('update:modelValue', v)"
  >
    <SwitchThumb
      class="block h-3.5 w-3.5 rounded-full bg-text-1 shadow-sm
             transition-transform translate-x-0.5
             data-[state=checked]:translate-x-[18px]"
    />
  </SwitchRoot>
</template>
